import React, { useState, useEffect, Suspense } from "react";
import * as mui from "@mui/material";
import ResponsiveContainer from "../../layout/Container";
import SearchAppBar from "../../common/Appbar";
import userService from "../../../services/UserService";
import auditService from "../../../services/AuditService";
import Separator from "../../layout/Separator";
import ErrorBoundary from "../../common/ErrorBoundery";
import {
  DataGrid,
  GridToolbar,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExport,
} from "@mui/x-data-grid";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useNavigate } from "react-router-dom";
import ClientSideBar from "./ClientSideBar";
import {
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  Pending as PendingIcon,
  ErrorOutline as ErrorOutlineIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
} from "@mui/icons-material";
import DynamicSnackbar from "../../common/Snackbar";
import appService from "../../../services/ApplicationService";

const theme = createTheme({
  palette: {
    primary: {
      main: "#2196F3",
    },
    secondary: {
      main: "#FF9800",
    },
    error: {
      main: "#f44336",
    },
    success: {
      main: "#4CAF50",
    },
  },
  typography: {
    h6: {
      fontSize: "1rem",
      fontWeight: 600,
    },
    h4: {
      fontSize: "2rem",
      fontWeight: 600,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 6px 12px rgba(0, 0, 0, 0.15)",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
        },
      },
    },
  },
});

const LoadingSkeleton = () => (
  <mui.Box sx={{ width: '100%', mt: 2 }}>
    {/* Summary Cards Loading */}
    <mui.Grid container spacing={2} sx={{ mb: 3 }}>
      {[1, 2, 3, 4, 5].map((item) => (
        <mui.Grid item xs={12} sm={6} md={item === 1 ? 3 : 2} key={item}>
          <mui.Skeleton 
            variant="rectangular" 
            height={100} 
            sx={{ 
              borderRadius: 2,
              animation: 'wave'
            }} 
          />
        </mui.Grid>
      ))}
    </mui.Grid>

    {/* Table Loading */}
    <mui.Paper sx={{ p: 2, borderRadius: 2 }}>
      <mui.Skeleton height={52} sx={{ mb: 1 }} /> {/* Header */}
      {[...Array(5)].map((_, idx) => (
        <mui.Skeleton 
          key={idx} 
          height={52} 
          sx={{ 
            my: 0.5,
            animation: `wave ${(idx + 1) * 0.1}s`
          }} 
        />
      ))}
    </mui.Paper>
  </mui.Box>
);

const RequestDashboard = () => {
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [apps, setApps] = useState({});

  const navigate = useNavigate();

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchAuditRequests();
    }
  }, [currentUser]);

  useEffect(() => {
    filterRequests(selectedFilter);
  }, [requests, selectedFilter]);

  useEffect(() => {
    fetchApps();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const user = await userService.fetchCurrentUser();
      if (user) {
        setCurrentUser(user);
      }
    } catch (error) {
      console.error("Error fetching current user:", error);
      setError("Failed to fetch user information");
    }
  };

  const fetchAuditRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Add artificial delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const response = await auditService.getAuditRequests();

      if (response && Array.isArray(response.data)) {
        const userRequests = response.data
          .filter((request) => request.ASSIGNEE === currentUser.email)
          .map((request) => ({
            ...request,
            id: request.id || request.REQUEST_ID,
          }));
        setRequests(userRequests);
      } else {
        setRequests([]);
        setError("No audit requests found or invalid response format");
      }
    } catch (error) {
      console.error("Error fetching audit requests:", error);
      setError("Failed to fetch audit requests. Please try again later.");
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const filterRequests = (status) => {
    if (status === "All") {
      setFilteredRequests(requests);
    } else {
      setFilteredRequests(
        requests.filter((request) => request.STATUS === status)
      );
    }
  };

  const handleFilterClick = (status) => {
    setSelectedFilter(status === selectedFilter ? "All" : status);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Completed":
        return <CheckCircleIcon style={{ color: "#4CAF50" }} />;
      case "In Review":
        return <AssignmentTurnedInIcon style={{ color: "#2196F3" }} />;
      case "Evidence Submitted":
        return <AssignmentTurnedInIcon style={{ color: "#FF9800" }} />;
      case "Resubmission Needed":
        return <ErrorOutlineIcon style={{ color: "#f44336" }} />;
      case "Pending":
      default:
        return <PendingIcon style={{ color: "#757575" }} />;
    }
  };

  const calculateAging = (params) => {
    console.log("Full row data:", params);

    // Get the due date directly from the row
    const dueDate = params.row.DUE_DATE;
    console.log("Found due date:", dueDate);

    if (!dueDate) {
      console.log("No due date found");
      return null;
    }

    const dueDateObj = new Date(dueDate);
    console.log("Parsed due date:", dueDateObj);

    if (isNaN(dueDateObj.getTime())) {
      console.log("Invalid date");
      return null;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dueDateObj.setHours(0, 0, 0, 0);

    const diffTime = dueDateObj.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    console.log("Calculated days:", diffDays);
    return diffDays;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "#4CAF50";
      case "In Review":
        return "#2196F3";
      case "Evidence Submitted":
        return "#FF9800";
      case "Resubmission Needed":
        return "#f44336";
      case "Pending":
      default:
        return "#757575";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "High":
        return "#f44336";
      case "Medium":
        return "#FF9800";
      case "Low":
        return "#4CAF50";
      default:
        return "#757575";
    }
  };

  const fetchApps = async () => {
    try {
      const response = await appService.fetchApps();
      if (response && Array.isArray(response)) {
        const appsMap = response.reduce((acc, app) => {
          acc[app.id] = app.APP_NAME;
          return acc;
        }, {});
        setApps(appsMap);
      }
    } catch (error) {
      console.error("Error fetching apps:", error);
    }
  };

  const CustomGridToolbar = () => {
    return (
      <mui.Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <mui.Box sx={{ display: "flex", gap: 1 }}>
          <GridToolbarColumnsButton />
          <GridToolbarFilterButton />
          <GridToolbarExport
            printOptions={{ disableToolbarButton: true }}
            csvOptions={{
              fileName: "audit-requests-export",
              delimiter: ",",
              utf8WithBom: true,
            }}
          />
        </mui.Box>
      </mui.Box>
    );
  };

  const columns = [
    {
      field: "REQUEST_ID",
      headerName: "Request ID",
      width: 130,
      renderCell: (params) => (
        <Tooltip title="View Details">
          <mui.Link
            component="button"
            variant="body2"
            onClick={() => navigate(`/myrequests/details/${params.row.id}`)}
            style={{
              textDecoration: "none",
              display: "flex",
              alignItems: "center",
            }}
          >
            {params.value}
            <OpenInNewIcon style={{ marginLeft: 5, fontSize: 16 }} />
          </mui.Link>
        </Tooltip>
      ),
    },
    {
      field: "SYSTEM_NAME",
      headerName: "System",
      width: 200,
    },
    {
      field: "REQUEST_TYPE",
      headerName: "Type",
      width: 150,
    },
    {
      field: "STATUS",
      headerName: "Status",
      width: 180,
      renderCell: (params) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {getStatusIcon(params.value)}
          <span style={{ color: getStatusColor(params.value) }}>
            {params.value}
          </span>
        </div>
      ),
    },
    {
      field: "PRIORITY",
      headerName: "Priority",
      width: 120,
      renderCell: (params) => (
        <mui.Chip
          label={params.value}
          size="small"
          style={{
            backgroundColor: getPriorityColor(params.value),
            color: "white",
          }}
        />
      ),
    },
    {
      field: "DUE_DATE",
      headerName: "Due Date",
      width: 120,
      valueFormatter: (params) => {
        if (!params.value) return "";
        return new Date(params.value).toLocaleDateString();
      },
    },
    {
      field: "aging",
      headerName: "Aging",
      flex: 0.8,
      minWidth: 120,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const dueDate = params.row?.DUE_DATE;

        if (!dueDate) {
          return (
            <mui.Typography
              variant="body2"
              sx={{
                fontSize: "0.8125rem",
                color: "text.secondary",
              }}
            >
              -
            </mui.Typography>
          );
        }

        const dueDateObj = new Date(dueDate);
        if (isNaN(dueDateObj.getTime())) {
          return (
            <mui.Typography
              variant="body2"
              sx={{
                fontSize: "0.8125rem",
                color: "text.secondary",
              }}
            >
              -
            </mui.Typography>
          );
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        dueDateObj.setHours(0, 0, 0, 0);

        const diffTime = dueDateObj.getTime() - today.getTime();
        const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        const absDays = Math.abs(days);

        let color = theme.palette.text.secondary;
        let text = "";

        if (days < 0) {
          color = theme.palette.error.main;
          text = `${absDays} ${absDays === 1 ? 'day' : 'days'} overdue`;
        } else if (days === 0) {
          color = theme.palette.warning.main;
          text = "Due today";
        } else {
          if (days >= 5) {
            color = theme.palette.success.main;
          } else if (days <= 3) {
            color = theme.palette.warning.main;
          }
          text = `${days} ${days === 1 ? 'day' : 'days'} left`;
        }

        return (
          <mui.Typography
            variant="body2"
            sx={{
              fontSize: "0.8125rem",
              color,
              fontWeight: 500,
            }}
          >
            {text}
          </mui.Typography>
        );
      },
    },
  ];

  const SummaryCard = ({ icon: Icon, title, count, status, color }) => (
    <mui.Card
      onClick={() => handleFilterClick(status)}
      sx={{
        cursor: "pointer",
        transition: "all 0.3s ease",
        background:
          selectedFilter === status
            ? `linear-gradient(135deg, ${color}15, ${color}30)`
            : "background.paper",
        border: "none",
        borderRadius: 2,
        position: "relative",
        overflow: "hidden",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: selectedFilter === status ? 2 : 1,
          "&::before": {
            opacity: 1,
          },
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "4px",
          height: "100%",
          background: color,
          opacity: selectedFilter === status ? 1 : 0,
          transition: "opacity 0.3s ease",
        },
      }}
    >
      <mui.CardContent sx={{ p: 2 }}>
        <mui.Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 1.5,
            position: "relative",
          }}
        >
          <mui.Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 1.5,
              p: 0.75,
              background: `${color}15`,
              mr: 1.5,
            }}
          >
            <Icon sx={{ color: color, fontSize: 20 }} />
          </mui.Box>
          <mui.Typography
            variant="subtitle2"
            sx={{
              fontSize: "0.875rem",
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            {title}
          </mui.Typography>
        </mui.Box>
        <mui.Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
          <mui.Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: "text.primary",
              fontSize: "1.75rem",
              lineHeight: 1,
            }}
          >
            {count}
          </mui.Typography>
          <mui.Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontSize: "0.75rem",
            }}
          >
            requests
          </mui.Typography>
        </mui.Box>
      </mui.CardContent>
    </mui.Card>
  );

  const customMainContent = (
    <ThemeProvider theme={theme}>
      <div>
        <ResponsiveContainer>
          <mui.Breadcrumbs aria-label="breadcrumb">
            <mui.Link underline="hover" color="inherit" href="/dashboard">
              <i className="material-icons">home</i>
            </mui.Link>
            <mui.Link underline="hover" color="inherit" href="/dashboard">
              My Dashboard
            </mui.Link>

            <mui.Typography color="text.primary">My Requests</mui.Typography>
          </mui.Breadcrumbs>

          <Separator />

          {loading ? (
            <LoadingSkeleton />
          ) : (
            <>
              {currentUser && (
                <mui.Box 
                  sx={{ 
                    mb: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5
                  }}
                >
                  <mui.Typography
                    variant="h4"
                    sx={{
                      fontSize: '1.75rem',
                      fontWeight: 600,
                      color: 'text.primary',
                      letterSpacing: '-0.01em'
                    }}
                  >
                    Welcome back, {currentUser.first_name || 'User'}!
                  </mui.Typography>
                  <mui.Typography
                    variant="body1"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.95rem',
                      letterSpacing: '0.01em'
                    }}
                  >
                    Here's an overview of your audit requests and their current status
                  </mui.Typography>
                </mui.Box>
              )}

              <mui.Box
                sx={{
                  mb: 1,
                  background: "white",
                  borderRadius: 2,
                  p: 2.5,
                  boxShadow: "0 1px 8px rgba(0,0,0,0.02)",
                }}
              >
                <mui.Grid container spacing={2}>
                  <mui.Grid item xs={12} sm={6} md={3}>
                    <SummaryCard
                      icon={AccessTimeIcon}
                      title="Total Requests"
                      count={requests.length}
                      status="All"
                      color="#2196f3"
                    />
                  </mui.Grid>
                  <mui.Grid item xs={12} sm={6} md={2}>
                    <SummaryCard
                      icon={PendingIcon}
                      title="Pending"
                      count={requests.filter((r) => r.STATUS === "Pending").length}
                      status="Pending"
                      color="#ff9800"
                    />
                  </mui.Grid>
                  <mui.Grid item xs={12} sm={6} md={2}>
                    <SummaryCard
                      icon={AssignmentTurnedInIcon}
                      title="In Review"
                      count={
                        requests.filter((r) => r.STATUS === "In Review").length
                      }
                      status="In Review"
                      color="#03a9f4"
                    />
                  </mui.Grid>
                  <mui.Grid item xs={12} sm={6} md={2}>
                    <SummaryCard
                      icon={ErrorOutlineIcon}
                      title="Resubmission"
                      count={
                        requests.filter((r) => r.STATUS === "Resubmission Needed")
                          .length
                      }
                      status="Resubmission Needed"
                      color="#f44336"
                    />
                  </mui.Grid>
                  <mui.Grid item xs={12} sm={6} md={3}>
                    <SummaryCard
                      icon={CheckCircleIcon}
                      title="Completed"
                      count={
                        requests.filter((r) => r.STATUS === "Completed").length
                      }
                      status="Completed"
                      color="#4caf50"
                    />
                  </mui.Grid>
                </mui.Grid>
              </mui.Box>

              {error && (
                <mui.Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </mui.Alert>
              )}

              <mui.Paper
                sx={{
                  borderRadius: 2,
                  boxShadow: "none",
                  border: "1px solid #e0e0e0",
                  overflow: "hidden",
                }}
              >
                <DataGrid
                  rows={filteredRequests}
                  columns={[
                    {
                      field: "REQUEST_ID",
                      headerName: "Request ID",
                      flex: 0.8,
                      minWidth: 120,
                      renderCell: (params) => (
                        <Tooltip title="View Details">
                          <mui.Link
                            component="button"
                            variant="body2"
                            onClick={() =>
                              navigate(`/myrequests/details/${params.row.id}`)
                            }
                            sx={{
                              textDecoration: "none",
                              display: "flex",
                              alignItems: "center",
                              fontSize: "0.8125rem",
                            }}
                          >
                            {params.value}
                            <OpenInNewIcon sx={{ ml: 0.5, fontSize: 16 }} />
                          </mui.Link>
                        </Tooltip>
                      ),
                    },
                    {
                      field: "SYSTEM_NAME",
                      headerName: "System",
                      flex: 1,
                      minWidth: 150,
                      renderCell: (params) => (
                        <mui.Typography
                          variant="body2"
                          sx={{ fontSize: "0.8125rem" }}
                        >
                          {apps[params.value] || "N/A"}
                        </mui.Typography>
                      ),
                    },
                    {
                      field: "DESCRIPTION",
                      headerName: "Description",
                      flex: 2,
                      minWidth: 200,
                      renderCell: (params) => (
                        <mui.Tooltip
                          title={params?.row?.DESCRIPTION || "N/A"}
                          arrow
                        >
                          <mui.Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.8125rem",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {params?.row?.DESCRIPTION || "N/A"}
                          </mui.Typography>
                        </mui.Tooltip>
                      ),
                    },
                    {
                      field: "REQUEST_TYPE",
                      headerName: "Type",
                      flex: 0.8,
                      minWidth: 120,
                      renderCell: (params) => (
                        <mui.Typography
                          variant="body2"
                          sx={{ fontSize: "0.8125rem" }}
                        >
                          {params.value || "N/A"}
                        </mui.Typography>
                      ),
                    },
                    {
                      field: "STATUS",
                      headerName: "Status",
                      flex: 0.8,
                      minWidth: 120,
                      align: "center",
                      headerAlign: "center",
                      renderCell: (params) => (
                        <mui.Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                            gap: 0.5,
                          }}
                        >
                          <mui.Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              backgroundColor: getStatusColor(params.value),
                              flexShrink: 0,
                            }}
                          />
                          <mui.Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.8125rem",
                              color: "text.secondary",
                            }}
                          >
                            {params.value}
                          </mui.Typography>
                        </mui.Box>
                      ),
                    },
                    {
                      field: "PRIORITY",
                      headerName: "Priority",
                      flex: 0.7,
                      minWidth: 90,
                      align: "center",
                      headerAlign: "center",
                      renderCell: (params) => (
                        <mui.Typography
                          variant="body2"
                          sx={{
                            fontSize: "0.8125rem",
                            color: "text.secondary",
                          }}
                        >
                          {params.value || "N/A"}
                        </mui.Typography>
                      ),
                    },
                    {
                      field: "DUE_DATE",
                      headerName: "Due Date",
                      flex: 0.8,
                      minWidth: 100,
                      align: "center",
                      headerAlign: "center",
                      renderCell: (params) => (
                        <mui.Typography
                          variant="body2"
                          sx={{
                            fontSize: "0.8125rem",
                            color: "text.secondary",
                          }}
                        >
                          {params.value
                            ? new Date(params.value).toLocaleDateString()
                            : "N/A"}
                        </mui.Typography>
                      ),
                    },
                    {
                      field: "aging",
                      headerName: "Aging",
                      flex: 0.8,
                      minWidth: 120,
                      align: "center",
                      headerAlign: "center",
                      renderCell: (params) => {
                        const dueDate = params.row?.DUE_DATE;

                        if (!dueDate) {
                          return (
                            <mui.Typography
                              variant="body2"
                              sx={{
                                fontSize: "0.8125rem",
                                color: "text.secondary",
                              }}
                            >
                              -
                            </mui.Typography>
                          );
                        }

                        const dueDateObj = new Date(dueDate);
                        if (isNaN(dueDateObj.getTime())) {
                          return (
                            <mui.Typography
                              variant="body2"
                              sx={{
                                fontSize: "0.8125rem",
                                color: "text.secondary",
                              }}
                            >
                              -
                            </mui.Typography>
                          );
                        }

                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        dueDateObj.setHours(0, 0, 0, 0);

                        const diffTime = dueDateObj.getTime() - today.getTime();
                        const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                        const absDays = Math.abs(days);

                        let color = theme.palette.text.secondary;
                        let text = "";

                        if (days < 0) {
                          color = theme.palette.error.main;
                          text = `${absDays} ${absDays === 1 ? 'day' : 'days'} overdue`;
                        } else if (days === 0) {
                          color = theme.palette.warning.main;
                          text = "Due today";
                        } else {
                          if (days >= 5) {
                            color = theme.palette.success.main;
                          } else if (days <= 3) {
                            color = theme.palette.warning.main;
                          }
                          text = `${days} ${days === 1 ? 'day' : 'days'} left`;
                        }

                        return (
                          <mui.Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.8125rem",
                              color,
                              fontWeight: 500,
                            }}
                          >
                            {text}
                          </mui.Typography>
                        );
                      },
                    },
                  ]}
                  pageSize={10}
                  rowsPerPageOptions={[10, 25, 50]}
                  checkboxSelection={false}
                  disableSelectionOnClick
                  loading={loading}
                  autoHeight
                  slots={{
                    toolbar: CustomGridToolbar,
                  }}
                  slotProps={{
                    toolbar: {
                      showQuickFilter: false,
                    },
                  }}
                  sx={{
                    border: "none",
                    width: "100%",
                    "& .MuiDataGrid-main": {
                      borderRadius: 0,
                      width: "100%",
                    },
                    "& .MuiDataGrid-virtualScroller": {
                      marginTop: "56px !important",
                    },
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: "#f5f5f5",
                      minHeight: "56px !important",
                      maxHeight: "56px !important",
                      lineHeight: "56px",
                      borderBottom: "1px solid rgba(224, 224, 224, 1)",
                    },
                    "& .MuiDataGrid-columnHeader": {
                      backgroundColor: "#f5f5f5",
                      height: "56px !important",
                      maxHeight: "56px !important",
                      "&:focus": {
                        outline: "none",
                      },
                      "&:hover": {
                        backgroundColor: "#eeeeee",
                      },
                    },
                    "& .MuiDataGrid-columnHeaderTitle": {
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      color: "rgba(0, 0, 0, 0.87)",
                    },
                    "& .MuiDataGrid-cell": {
                      borderBottom: "1px solid rgba(224, 224, 224, 1)",
                      padding: "8px 16px",
                    },
                    "& .MuiDataGrid-row": {
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                    },
                    "& .MuiDataGrid-footerContainer": {
                      borderTop: "1px solid rgba(224, 224, 224, 1)",
                    },
                  }}
                />
              </mui.Paper>
            </>
          )}
        </ResponsiveContainer>

        <DynamicSnackbar
          open={snackbarOpen}
          message={snackbarMessage}
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
        />
      </div>
    </ThemeProvider>
  );

  return (
    <ErrorBoundary fallback={<p>Failed to load data.</p>}>
      <Suspense fallback="Loading...">
        <div>
          <ClientSideBar mainContent={customMainContent} />
        </div>
      </Suspense>
    </ErrorBoundary>
  );
};

export default RequestDashboard;